import { useState, useEffect, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Bell, CheckCheck, Clock, Star, CreditCard, Calendar, AlertCircle, Filter, User, Megaphone, XCircle, FileText } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import api from "@/lib/api";
import { formatDistanceToNow } from "date-fns";
import { toast } from "sonner";

const notificationIcons = {
  complete_profile: { icon: User, color: "bg-violet-100 text-violet-600" },
  complete_payment: { icon: CreditCard, color: "bg-teal-100 text-teal-600" },
  new_tour_request: { icon: Calendar, color: "bg-blue-100 text-blue-600" },
  tour_assigned: { icon: Calendar, color: "bg-indigo-100 text-indigo-600" },
  tour_unassigned: { icon: XCircle, color: "bg-orange-100 text-orange-600" },
  tour_cancelled: { icon: AlertCircle, color: "bg-red-100 text-red-600" },
  payment_processed: { icon: CreditCard, color: "bg-emerald-100 text-emerald-600" },
  new_review: { icon: Star, color: "bg-amber-100 text-amber-600" },
  announcement: { icon: Megaphone, color: "bg-pink-100 text-pink-600" },
  reminder: { icon: Clock, color: "bg-violet-100 text-violet-600" },
  // Fallback mappings for older notification types
  new_booking: { icon: Calendar, color: "bg-blue-100 text-blue-600" },
  booking_offered: { icon: Calendar, color: "bg-indigo-100 text-indigo-600" },
  review_received: { icon: Star, color: "bg-amber-100 text-amber-600" },
  tour_reminder: { icon: Clock, color: "bg-violet-100 text-violet-600" },
  status_changed: { icon: AlertCircle, color: "bg-orange-100 text-orange-600" },
};

const FILTER_OPTIONS = [
  { value: "all", label: "All Notifications" },
  { value: "complete_profile", label: "Complete Profile" },
  { value: "complete_payment", label: "Complete Payment" },
  { value: "new_tour_request", label: "New Tour Request" },
  { value: "tour_assigned", label: "Tour Assigned" },
  { value: "tour_unassigned", label: "Tour Unassigned" },
  { value: "tour_cancelled", label: "Tour Cancelled" },
  { value: "payment_processed", label: "Payment Processed" },
  { value: "new_review", label: "New Review" },
  { value: "announcement", label: "Announcements" },
  { value: "reminder", label: "Reminders" },
];

export default function NotificationsPage() {
  const { t } = useTranslation();
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [filter, setFilter] = useState("all");
  const [loading, setLoading] = useState(true);

  const fetchNotifications = useCallback(async () => {
    try {
      const { data } = await api.get("/notifications", { params: { type: filter !== "all" ? filter : undefined } });
      setNotifications(data.notifications);
      setUnreadCount(data.unread_count);
    } catch {
      toast.error("Failed to load notifications");
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => {
    fetchNotifications();
  }, [fetchNotifications]);

  const markRead = async (id) => {
    try {
      await api.put(`/notifications/${id}/read`);
      setNotifications(prev => prev.map(n => n.id === id ? { ...n, read: true } : n));
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch { /* silent */ }
  };

  const markAllRead = async () => {
    try {
      await api.put("/notifications/read-all");
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
      setUnreadCount(0);
      toast.success("All notifications marked as read");
    } catch {
      toast.error("Failed to mark all as read");
    }
  };

  const getIcon = (notification) => {
    const type = notification.notification_category || notification.type;
    const config = notificationIcons[type] || notificationIcons.new_booking;
    const IconComponent = config.icon;
    return (
      <div className={`w-10 h-10 rounded-full ${config.color} flex items-center justify-center shrink-0`}>
        <IconComponent className="w-5 h-5" />
      </div>
    );
  };

  const formatTime = (timestamp) => {
    try {
      return formatDistanceToNow(new Date(timestamp), { addSuffix: true });
    } catch {
      return "Just now";
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-2 border-[#1A1B2E] border-t-transparent" />
      </div>
    );
  }

  return (
    <div className="animate-slideUp space-y-6" data-testid="notifications-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <p className="text-sm text-slate-500">{t("notifications.description")}</p>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2">
            <Filter className="w-4 h-4 text-slate-400" />
            <Select value={filter} onValueChange={setFilter}>
              <SelectTrigger className="w-[180px]" data-testid="notification-filter">
                <SelectValue placeholder="Filter" />
              </SelectTrigger>
              <SelectContent>
                {FILTER_OPTIONS.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          {unreadCount > 0 && (
            <Button
              variant="outline"
              size="sm"
              onClick={markAllRead}
              className="text-sm"
              data-testid="mark-all-read-btn"
            >
              <CheckCheck className="w-4 h-4 mr-2" />
              {t("notifications.markAllRead")}
            </Button>
          )}
        </div>
      </div>

      <div className="bg-white border border-slate-100 rounded-xl shadow-sm overflow-hidden">
        {notifications.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-slate-400">
            <Bell className="w-12 h-12 mb-4 opacity-50" />
            <p className="text-sm">{t("notifications.noNotifications")}</p>
          </div>
        ) : (
          <div className="divide-y divide-slate-100">
            {notifications.map((notification) => (
              <div
                key={notification.id}
                className={`flex items-start gap-4 p-4 transition-colors cursor-pointer hover:bg-slate-50 ${
                  !notification.read ? "bg-blue-50/50" : ""
                }`}
                onClick={() => !notification.read && markRead(notification.id)}
                data-testid={`notification-${notification.id}`}
              >
                {getIcon(notification)}
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-2">
                    <h4 className={`text-sm ${!notification.read ? "font-semibold text-slate-900" : "font-medium text-slate-700"}`}>
                      {notification.title}
                    </h4>
                    <span className="text-xs text-slate-400 whitespace-nowrap">
                      {formatTime(notification.created_at)}
                    </span>
                  </div>
                  <p className="text-sm text-slate-500 mt-0.5 line-clamp-2">{notification.message}</p>
                </div>
                {!notification.read && (
                  <div className="w-2 h-2 rounded-full bg-blue-500 mt-2 shrink-0" />
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
